import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionType, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * CAS-IN / CAS-OUT: Side-hinged casement window.
 * Pivot group at hinge edge for Y-axis rotation.
 */
export class CasementSection implements SectionInterface {
  readonly group: THREE.Group;
  private pivotGroup: THREE.Group;
  private hingeSide: 'left' | 'right';
  private swingDir: 'inward' | 'outward';

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-cas-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: config.type };

    this.hingeSide = config.orientation.hingeSide ?? 'left';
    this.swingDir = config.type === SectionType.CAS_IN ? 'inward' : 'outward';

    // Effective dimensions after insetting from adjacent mullions
    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const clearW = effW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;

    // Build sash + glass + handle as one assembly
    const sashAssembly = new THREE.Group();
    sashAssembly.name = 'sash-assembly';

    // Sash frame built to effective dimensions
    const sash = createSash(effW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial);
    sashAssembly.add(sash);

    // Glass centered in effective opening
    const glass = createGlassPane(
      clearW, clearH, PROFILE.glassThickness,
      materialLib.glass(config.infill.tint, config.infill.glazingTier),
    );
    glass.position.set(effW / 2, effH / 2, 0);
    sashAssembly.add(glass);

    // Roeden
    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(effW / 2, effH / 2, 0);
      sashAssembly.add(roeden);
    }

    // Handle on opposite side of hinge, relative to effective sash
    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    const handleX = this.hingeSide === 'left'
      ? effW - PROFILE.sashWidth / 2
      : PROFILE.sashWidth / 2;
    handle.position.set(handleX, effH / 2, PROFILE.sashDepth / 2);
    sashAssembly.add(handle);

    // Pivot at hinge edge (inner face of adjacent mullion/frame)
    this.pivotGroup = new THREE.Group();
    this.pivotGroup.name = 'pivot';

    if (this.hingeSide === 'left') {
      this.pivotGroup.position.set(insets.left, 0, 0);
      sashAssembly.position.set(0, insets.bottom, 0);
    } else {
      this.pivotGroup.position.set(insets.left + effW, 0, 0);
      sashAssembly.position.set(-effW, insets.bottom, 0);
    }

    this.pivotGroup.add(sashAssembly);
    this.group.add(this.pivotGroup);

    // Apply initial open state
    if (config.openAmount > 0) {
      this.setOpenAngle(config.openAmount * (Math.PI / 2));
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setOpenAngle(rad: number): void {
    const sign = this.hingeSide === 'left' ? -1 : 1;
    const zSign = this.swingDir === 'inward' ? 1 : -1;
    this.pivotGroup.rotation.y = sign * zSign * rad;
  }
}
